import type { ClarityDialogWidth } from '../models/clarity-dialog-width.type';

/**
 * A Clarity dialog's geometry, stated here rather than left to Material, so the legacy `.ds`-scoped
 * dialog overrides cannot decide it. `dvw` rather than `vw` so a mobile browser's collapsing toolbar
 * does not change the dialog's width mid-scroll. Shared by both dialog backends, so a `passive` dialog
 * is the same box as a Material one.
 *
 * `widths` is keyed by `ClarityDialogWidth` and checked against it, so a word cannot be added to that
 * vocabulary without a width to open at. `maxWidths` caps each entry: a `wide` dialog on a narrow
 * viewport is `92dvw`, not 896 px of horizontal scroll; a `media` dialog takes its content's width up
 * to `80dvw`.
 *
 * `maxHeights` bounds the whole dialog — header, body and footer — per width; past it only the
 * frame's body scrolls. The bound reaches the surface through the panel-class rules in the frame's
 * stylesheet.
 */
export const CLARITY_DIALOG_SIZE = {
	widths: {
		default: '480px',
		wide: '896px',
		media: 'fit-content',
	},
	maxWidths: {
		default: '92dvw',
		wide: '92dvw',
		media: '80dvw',
	},
	maxHeights: {
		default: '95dvh',
		wide: '95dvh',
		media: '90dvh',
	},
} as const satisfies {
	readonly widths: Record<ClarityDialogWidth, string>;
	readonly maxWidths: Record<ClarityDialogWidth, string>;
	readonly maxHeights: Record<ClarityDialogWidth, string>;
};
